import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  DollarSign,
  CheckCircle,
  RefreshCw,
  Clock
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  unpaid: { label: 'Unpaid', color: 'bg-amber-100 text-amber-800', icon: Clock },
  paid: { label: 'Paid', color: 'bg-blue-100 text-blue-800', icon: DollarSign },
  confirmed: { label: 'Confirmed', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
};

const BrokerCommissions = () => {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);

  const fetchCommissions = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/commissions`);
      setCommissions(res.data.commissions || []);
    } catch (err) {
      console.error('Error fetching commissions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCommissions();
  }, [fetchCommissions]);

  const handleConfirm = async (commId) => {
    setConfirming(commId);
    try {
      await axios.put(`${BACKEND_URL}/api/commissions/${commId}`, { status: 'confirmed' });
      fetchCommissions();
    } catch (err) {
      console.error('Error confirming commission:', err);
      alert(err.response?.data?.detail || 'Failed to confirm commission');
    } finally {
      setConfirming(null);
    }
  };

  const fmt = (val) => {
    if (val == null || val === '') return '—';
    return `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-teal-600" />
      </div>
    );
  }

  // Summary stats
  const totalCommission = commissions.reduce((sum, c) => sum + (c.commission_amount || 0), 0);
  const paidCount = commissions.filter(c => c.status === 'paid').length;
  const confirmedCount = commissions.filter(c => c.status === 'confirmed').length;
  const unpaidCount = commissions.filter(c => c.status === 'unpaid').length;

  return (
    <div className="space-y-6">
      {/* Header + Refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-800" data-testid="broker-commissions-title">My Commissions</h3>
          <p className="text-sm text-slate-500">{commissions.length} commission{commissions.length !== 1 ? 's' : ''} assigned to you</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchCommissions} data-testid="broker-refresh-commissions">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-xs font-semibold tracking-widest uppercase text-slate-400">Total</p>
            <p className="text-xl font-bold text-emerald-700 mt-1" data-testid="broker-total-commission">{fmt(totalCommission)}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-xs font-semibold tracking-widest uppercase text-slate-400">Unpaid</p>
            <p className="text-xl font-bold text-amber-600 mt-1" data-testid="broker-unpaid-count">{unpaidCount}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-xs font-semibold tracking-widest uppercase text-slate-400">Paid</p>
            <p className="text-xl font-bold text-blue-600 mt-1" data-testid="broker-paid-count">{paidCount}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-xs font-semibold tracking-widest uppercase text-slate-400">Confirmed</p>
            <p className="text-xl font-bold text-emerald-600 mt-1" data-testid="broker-confirmed-count">{confirmedCount}</p>
          </CardContent>
        </Card>
      </div>

      {/* Commissions List */}
      {commissions.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <DollarSign className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 mb-1">No commissions yet</p>
            <p className="text-sm text-slate-400">Commission records assigned to you will appear here.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="broker-commissions-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Borrower</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Date Funded</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Program</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Loan/LOC Draw</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Amount Funded</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Commission</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.map((comm) => {
                    const s = STATUS_MAP[comm.status] || STATUS_MAP.unpaid;
                    return (
                      <tr key={comm.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`broker-commission-row-${comm.id}`}>
                        <td className="px-4 py-3 font-medium text-slate-800">{comm.borrower_company_name}</td>
                        <td className="px-4 py-3 text-slate-600">{comm.date_funded || '—'}</td>
                        <td className="px-4 py-3 text-slate-600">{comm.program || '—'}</td>
                        <td className="px-4 py-3 text-slate-600">{comm.loan_loc_draw || '—'}</td>
                        <td className="px-4 py-3 text-right text-slate-700 font-medium">{fmt(comm.amount_funded)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-emerald-700">{fmt(comm.commission_amount)}</td>
                        <td className="px-4 py-3">
                          <Badge className={`${s.color} text-xs`}>{s.label}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {comm.status === 'paid' ? (
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => handleConfirm(comm.id)}
                              disabled={confirming === comm.id}
                              data-testid={`confirm-commission-${comm.id}`}
                            >
                              {confirming === comm.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                              ) : (
                                <>
                                  <CheckCircle className="h-3.5 w-3.5 mr-1" /> Confirm
                                </>
                              )}
                            </Button>
                          ) : comm.status === 'confirmed' ? (
                            <span className="text-xs text-emerald-600 font-medium flex items-center justify-end gap-1">
                              <CheckCircle className="h-3.5 w-3.5" /> Confirmed
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">Pending payment</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BrokerCommissions;
